import type { InvestmentPortfolioResponse } from 'shared';

import { formatCurrency } from '../../lib/format/currency';
import { Table } from '../ui/Table';
import { TableCell } from '../ui/TableCell';
import { TableHeaderCell } from '../ui/TableHeaderCell';

type Returns = InvestmentPortfolioResponse['accountReturns'][number];
type CurrencyReturns = InvestmentPortfolioResponse['currencies'][number]['returns'];

function money(value: string | null, currency: string): string {
  return value === null ? '—' : formatCurrency(value, currency);
}

function signedPct(value: string | null): string {
  if (value === null) return '—';
  const number = Number(value);
  return `${number > 0 ? '+' : ''}${number.toFixed(1)}%`;
}

function toneClass(value: string | null): string {
  if (value === null) return 'text-text-muted';
  const number = Number(value);
  if (number > 0) return 'text-success';
  if (number < 0) return 'text-danger';
  return '';
}

function periodPct(returns: Returns | CurrencyReturns | undefined, period: 'YTD' | '1Y') {
  return returns?.periods.find((row) => row.period === period)?.returnPct ?? null;
}

const statusLabel = {
  COMPLETE: 'Complete',
  PARTIAL: 'Partial',
  UNAVAILABLE: 'Unavailable',
} as const;

function ReturnCells({ returns }: { returns: Returns | CurrencyReturns | undefined }) {
  const values = [
    returns?.gainPct ?? null,
    returns?.annualisedReturnPct ?? null,
    periodPct(returns, 'YTD'),
    periodPct(returns, '1Y'),
  ];
  return (
    <>
      {values.map((value, index) => (
        <TableCell key={index} numeric className={`py-1.5 ${toneClass(value)}`}>
          {signedPct(value)}
        </TableCell>
      ))}
      <TableCell className="py-1.5 text-xs text-text-muted">
        {returns ? statusLabel[returns.status] : '—'}
      </TableCell>
    </>
  );
}

/** Every long-term account on one screen, with the canonical combined row per currency. */
export function LongTermAccountsComparison({
  portfolio,
  onSelectAccount,
}: {
  portfolio: InvestmentPortfolioResponse;
  onSelectAccount: (accountId: string) => void;
}) {
  if (portfolio.accounts.length === 0) {
    return <p className="text-sm text-text-muted">No long-term accounts yet.</p>;
  }
  const currencies = [...new Set(portfolio.accounts.map((row) => row.account.currency))].sort();
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded border border-border">
        <Table className="min-w-[720px] text-sm" aria-label="Long-term accounts side by side">
          <thead className="bg-surface-raised">
            <tr>
              <TableHeaderCell>Account</TableHeaderCell>
              <TableHeaderCell numeric>Value</TableHeaderCell>
              <TableHeaderCell numeric>Cash</TableHeaderCell>
              <TableHeaderCell numeric>Gain</TableHeaderCell>
              <TableHeaderCell numeric>Gain %</TableHeaderCell>
              <TableHeaderCell numeric>Annualised</TableHeaderCell>
              <TableHeaderCell numeric>YTD</TableHeaderCell>
              <TableHeaderCell numeric>1Y</TableHeaderCell>
              <TableHeaderCell>History</TableHeaderCell>
            </tr>
          </thead>
          {currencies.map((currency) => {
            const rows = portfolio.accounts.filter((row) => row.account.currency === currency);
            const combined = portfolio.currencies.find((row) => row.currency === currency);
            return (
              <tbody key={currency}>
                {rows.map((row) => {
                  const returns = portfolio.accountReturns.find(
                    (item) => item.accountId === row.account.id,
                  );
                  return (
                    <tr key={row.account.id} className="border-t border-border">
                      <TableCell className="py-1.5">
                        <button
                          type="button"
                          onClick={() => onSelectAccount(row.account.id)}
                          className="text-left font-medium underline hover:decoration-primary"
                        >
                          {row.account.name}
                        </button>
                        <div className="text-xs text-text-muted">
                          {currency}
                          {row.valuation.available
                            ? row.valuation.stale
                              ? ' · stale valuation'
                              : ''
                            : ' · not valued'}
                        </div>
                      </TableCell>
                      <TableCell numeric className="py-1.5">
                        {money(row.accountValue, currency)}
                      </TableCell>
                      <TableCell numeric className="py-1.5">
                        {row.cashWeightPct === null
                          ? '—'
                          : `${Number(row.cashWeightPct).toFixed(1)}%`}
                      </TableCell>
                      <TableCell numeric className={`py-1.5 ${toneClass(returns?.gain ?? null)}`}>
                        {money(returns?.gain ?? null, currency)}
                      </TableCell>
                      <ReturnCells returns={returns} />
                    </tr>
                  );
                })}
                {rows.length > 1 && combined ? (
                  <tr className="border-t-2 border-border bg-surface-raised font-semibold">
                    <TableCell className="py-1.5">
                      All {currency} accounts
                      <div className="text-xs font-normal text-text-muted">
                        Combined, valued accounts only
                      </div>
                    </TableCell>
                    <TableCell numeric className="py-1.5">
                      {money(combined.accountValue, currency)}
                    </TableCell>
                    <TableCell numeric className="py-1.5">
                      {combined.cashWeightPct === null
                        ? '—'
                        : `${Number(combined.cashWeightPct).toFixed(1)}%`}
                    </TableCell>
                    <TableCell numeric className={`py-1.5 ${toneClass(combined.returns.gain)}`}>
                      {money(combined.returns.gain, currency)}
                    </TableCell>
                    <ReturnCells returns={combined.returns} />
                  </tr>
                ) : null}
              </tbody>
            );
          })}
        </Table>
      </div>
      <p className="text-xs text-text-muted">
        Gain and annualised return are since the imported history began and exclude contributions.
        YTD and 1Y are time-weighted between holdings snapshots. Currencies are never combined.
        Select an account name to manage its theses and allocation.
      </p>
    </div>
  );
}
